import React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from 'sonner';
import { getIconComponent } from "@/lib/statusIconsMap";
import { STATUS_COLORS } from "@/lib/colorsMap";

export function StatusSelect({ task, showLabel = false, onStatusChange, onTaskChange, className }) {
  const [open, setOpen] = useState(false);
  const { axiosInstance } = useAuth();

  const statuses = task?.availableStatuses?.reduce((acc, status) => ({
    ...acc,
    [status.id]: {
      name: status.name,
      icon: getIconComponent(status.icon),
      color: STATUS_COLORS[status.color]?.text
    }
  }), {});

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await axiosInstance.patch(`/tasks/${task.organization.slug}/${task.key}`, {
        status: newStatus.id
      });

      if (onStatusChange) {
        onStatusChange(newStatus);
      }

      if (onTaskChange) {
        onTaskChange(response.data);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  return (
    <div className={className} onClick={(e) => e.stopPropagation()} >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="h-7 px-1.5 -ml-1.5 flex items-center gap-2">
              {React.createElement(statuses[task.status?.id].icon, { className: `w-5 h-5 ${statuses[task.status?.id].color}` })}
              {statuses[task.status?.id].name}
            </Button>
            {showLabel}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" side="top" align="start">
          <Command>
            <CommandInput placeholder="Change status..." />
            <CommandList>
              <CommandEmpty>No status found.</CommandEmpty>
              <CommandGroup>
                {Object.entries(statuses).map(([key, value]) => (
                  <CommandItem
                    key={key}
                    value={key}
                    onSelect={() => {
                      handleStatusChange({
                        id: key,
                        name: value.name
                      });
                      setOpen(false);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className="h-7 px-1.5 flex items-center gap-2">
                        {React.createElement(value.icon, { className: `w-4 h-4 ${value.color}` })}
                        {value.name}
                      </Button>
                    </div>
                    {task.status.id === key && (
                      <Check className="ml-auto h-4 w-4" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
